import { useMemo } from "react";
import {
  BlackfairBrand,
  Button,
  Card,
  FavoriteButton,
  FeaturedOfferBrand,
  GoodDealBrand,
  InspectedBrand,
  MegafairBrand,
  Text,
} from "../../../elements";
import {
  CalendarIcon,
  Car360Icon,
  OdometerIcon,
  PinIcon,
} from "../../../icons";
import {
  currencyFormatter,
  odometerFormatter,
  truncateFormatter,
} from "../../../support";
import { classNames } from "../../../support/formatters/class-formatter";
import { Badges, type VehicleCardProps } from "../vehicle-card.component";
import { VehicleCardImageMobile } from "./vehicle-card-image-mobile.component";
import styles from "./vehicle-card-mobile.module.css";

export function VehicleCardMobile({
  title,
  price,
  badge,
  images,
  view360,
  noPhoto,
  inspected,
  odometer,
  location,
  description,
  year,
  url,
  isFavorite = false,
  onFavoriteClick = () => {},
  onCardClick,
  onImageRender,
  isGoodDeal,
  isCtaWhatsApp,
}: Readonly<VehicleCardProps>) {
  const image = useMemo(() => {
    return images?.[0] ?? noPhoto;
  }, [images, noPhoto]);

  return (
    <div className={styles.Container} onClick={() => onCardClick?.()}>
      <Card variant="horizontal" className={styles.Card}>
        <div className={styles.Head}>
          <VehicleCardImageMobile
            title={`${title} ${description}`}
            onImageRender={onImageRender}
            src={image}
            index={0}
          >
            {view360 && (
              <div className={styles.Car360Badge} data-qa="tag_visao360-mobile">
                <Car360Icon size={16} viewBox="0 0 16 16" color="#fff" />
              </div>
            )}

            {inspected && (
              <div
                className={styles.FixedBadge}
                data-qa="tag_vistoriado-mobile"
              >
                <InspectedBrand size="small" />
              </div>
            )}
          </VehicleCardImageMobile>
        </div>

        <div className={styles.Body}>
          <FavoriteButton
            isFavorite={isFavorite}
            onFavoriteClick={onFavoriteClick}
            dataQA="card_favoritar-mobile"
          />

          <div className={styles.TextContainer}>
            <a
              className={styles.Link}
              href={url}
              target="_blank"
              rel="nofollow"
            >
              {isGoodDeal && <GoodDealBrand size="small" />}
              <Text
                variant="body-bold-small"
                color="#2E2D37"
                className={styles.Title}
                as="h2"
              >
                {title}
              </Text>
            </a>
          </div>
          <div className={styles.TextContainer}>
            <Text
              color="#696977"
              className={styles.Description}
              data-capitalize="true"
              as="h3"
              title={description.toLocaleLowerCase()}
            >
              {truncateFormatter(description.toLocaleLowerCase(), 32)}
            </Text>
          </div>

          <div className={styles.BodyItem}>
            <div className={styles.BodyCell}>
              <CalendarIcon size={18} viewBox="0 0 18 18" />
              <Text color="#696977">{year}</Text>
            </div>
            <div className={styles.BodyCell}>
              <OdometerIcon size={18} viewBox="0 0 18 18" />
              <Text color="#696977">{odometerFormatter(odometer)}</Text>
            </div>
          </div>
          <div className={styles.BodyItem}>
            <div className={classNames(styles.BodyCell, styles.Location)}>
              <PinIcon size={18} viewBox="0 0 18 18" />
              <Text color="#696977">{location}</Text>
            </div>
          </div>

          <div className={styles.BodyItemSpaceBetween}>
            <Text variant="web-subtitle-medium" color="#2E2D37">
              {currencyFormatter(price)}
            </Text>

            {badge === Badges.MegaFair && <MegafairBrand size="small" />}
            {badge === Badges.BlackFair && <BlackfairBrand size="small" />}
            {badge === Badges.Featured && <FeaturedOfferBrand size="small" />}
          </div>
        </div>
      </Card>
      <div className={styles.CardFooter}>
        <Button
          variant="secondary"
          tag="a"
          href={`${url}?lkid=1446#VerParcelasCardRB`}
          target="_blank"
          rel="nofollow"
          className={styles.CardButton}
          dataQA="btn_ver_parcelas-mobile"
          size="medium"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="19"
            height="18"
            viewBox="0 0 19 18"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M13.3815 8.23881C13.36 7.58955 13.1896 6.9403 12.8697 6.35821L10.0545 1.23134C9.84123 0.850746 9.69194 0.425373 9.60664 0L9.47867 0.223881C8.77488 1.5 8.77488 3.04478 9.47867 4.3209L11.7393 8.41791C12.4431 9.69403 12.4431 11.2388 11.7393 12.5149L11.6114 12.7388C11.5261 12.3134 11.3768 11.8881 11.1635 11.5075L9.09479 7.74627L7.79384 5.35075C7.58057 4.97015 7.43128 4.54478 7.34597 4.1194L7.21801 4.34328C6.51422 5.59701 6.51422 7.16418 7.21801 8.41791L9.5 12.5149C10.2038 13.791 10.2038 15.3358 9.5 16.6119L9.37204 16.8358C9.28673 16.4104 9.13744 15.9851 8.92417 15.6045L6.109 10.4776C5.72512 9.78358 5.5545 9.02239 5.59716 8.26119C2.59005 9.08955 0.5 10.8358 0.5 12.8731C0.5 15.694 4.53081 18 9.5 18C14.4692 18 18.5 15.7164 18.5 12.8731C18.4785 10.8134 16.41 9.06716 13.3815 8.23881Z"
              fill="currentColor"
              style={{ fill: "#E22446" }}
            />
          </svg>
          Financiar
        </Button>
        {isCtaWhatsApp ? (
          <Button
            variant="situational"
            tag="a"
            rel="nofollow"
            size="medium"
            className={classNames(styles.CardButton, styles.CardButtonExplore)}
            href={`${url}?openWhatsAppLeadModal=true`}
            data-qa="btn_whatsapp_rb_mobile"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.9105 4.1012C17.4742 5.66444 18.3333 7.74877 18.3333 9.96834C18.3333 14.5387 14.6278 18.2619 10.0792 18.2659C8.62231 18.2659 7.20505 17.884 5.9501 17.1601L1.6706 18.3335L2.81867 13.9341C2.1615 12.7209 1.81312 11.3566 1.81312 9.96834L1.81312 9.96436C1.81312 5.39396 5.51859 1.67081 10.0712 1.66683C12.2763 1.67081 14.3507 2.53397 15.9105 4.1012ZM3.21059 9.96834C3.21059 11.2094 3.54313 12.4226 4.16863 13.4807L4.31906 13.7313L3.63814 16.3407L6.17179 15.6446L6.43308 15.8076C7.52571 16.4998 8.78858 16.8617 10.0831 16.8617C13.8598 16.8617 16.9398 13.7671 16.9398 9.96436C16.9398 8.12268 16.2233 6.39237 14.9287 5.08767C13.6302 3.78696 11.9081 3.07097 10.0752 3.07097C6.2866 3.07494 3.21059 6.16961 3.21059 9.96834ZM8.34915 6.18553C8.45208 6.39237 8.95089 7.41066 9.02215 7.6016C9.08945 7.79253 9.12508 7.93175 8.91922 8.06699C8.71336 8.20621 8.24622 8.60398 8.11162 8.72331C7.97306 8.84264 7.95722 8.96595 8.06015 9.1728C8.16308 9.37964 8.38082 10.0479 9.08549 10.8395C9.63181 11.456 10.3088 11.8697 10.5146 11.993C10.7205 12.1123 10.8313 12.0049 10.9343 11.9015C11.0253 11.81 11.1758 11.6947 11.2945 11.5913C11.4133 11.4878 11.5004 11.452 11.6389 11.3844C11.7775 11.3168 11.8963 11.3486 11.9992 11.4003C12.1021 11.452 13.1195 11.8657 13.5313 12.0407C13.9351 12.2078 13.8796 12.3788 13.8836 12.5061C13.8915 12.6255 13.8915 12.7647 13.8915 12.9039C13.8915 13.0431 13.84 13.2659 13.6342 13.4568C13.4283 13.6477 12.9295 14.1808 11.9121 14.1808C10.8947 14.1808 9.91684 13.4369 9.77828 13.3335C9.63973 13.2301 7.55738 11.8737 6.66269 9.79332C6.44891 9.30008 6.32223 8.91424 6.22722 8.61194C6.06886 8.11472 6.09262 7.66524 6.14408 7.30724C6.20346 6.90947 6.64289 6.0821 7.12587 5.90708C7.60885 5.73604 8.02057 5.73604 8.10766 5.78775C8.19475 5.83946 8.24622 5.97868 8.34915 6.18553Z"
                fill="white"
              />
            </svg>
            WhatsApp
          </Button>
        ) : (
          <Button
            variant="situational"
            tag="a"
            target="_blank"
            rel="nofollow"
            size="medium"
            className={classNames(styles.CardButton, styles.CardButtonExplore)}
            href={`${url}?openLeadModal=true`}
            data-qa="btn_mensagem_rb_mobile"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M19.5035 14C19.5035 15.103 18.6065 16 17.5035 16H7.50049C7.23449 16 6.98049 16.105 6.79349 16.293L5.50049 17.586V8C5.50049 6.897 6.39749 6 7.50049 6H17.5035C18.6065 6 19.5035 6.897 19.5035 8V14ZM17.5035 4H7.50049C5.29449 4 3.50049 5.794 3.50049 8V20C3.50049 20.404 3.74349 20.769 4.11749 20.924C4.24149 20.975 4.37149 21 4.50049 21C4.76049 21 5.01549 20.898 5.20749 20.707L7.91449 18H17.5035C19.7095 18 21.5035 16.206 21.5035 14V8C21.5035 5.794 19.7095 4 17.5035 4ZM16.5001 8H8.50009C7.94809 8 7.50009 8.448 7.50009 9C7.50009 9.552 7.94809 10 8.50009 10H16.5001C17.0521 10 17.5001 9.552 17.5001 9C17.5001 8.448 17.0521 8 16.5001 8ZM14.5001 12H8.50009C7.94809 12 7.50009 12.448 7.50009 13C7.50009 13.552 7.94809 14 8.50009 14H14.5001C15.0521 14 15.5001 13.552 15.5001 13C15.5001 12.448 15.0521 12 14.5001 12Z"
                fill="white"
              />
            </svg>
            Mensagem
          </Button>
        )}
      </div>
    </div>
  );
}
